/**
 * Session Type Display Formatter (Phase 9.3)
 * ============================================
 * Centralized, human-readable labels for session types —
 * including effective/fallback types from the split identity system.
 *
 * Usage:
 *   import { formatSessionLabel, getSessionSubtitle } from '../utils/formatSessionType';
 *   const label = formatSessionLabel(day);
 *   const subtitle = getSessionSubtitle(day);
 */

// Human-readable labels for effective_session_type variants.
// These ONLY appear when the backend reclassified the session.
const EFFECTIVE_TYPE_LABELS = {
  upper_pull_focus:     { title: 'Upper Body',  subtitle: 'Pull Focus' },
  upper_push_focus:     { title: 'Upper Body',  subtitle: 'Push Focus' },
  lower_hinge_focus:    { title: 'Lower Body',  subtitle: 'Hinge Focus' },
  lower_squat_focus:    { title: 'Lower Body',  subtitle: 'Squat Focus' },
  legs_hinge_focus:     { title: 'Legs',         subtitle: 'Hinge Focus' },
  legs_squat_focus:     { title: 'Legs',         subtitle: 'Squat Focus' },
  full_body_pull_focus: { title: 'Full Body',    subtitle: 'Pull Focus' },
  full_body_push_focus: { title: 'Full Body',    subtitle: 'Push Focus' },
  constrained_session:  { title: 'Adapted',      subtitle: 'Recovery-Safe' },
};

/**
 * Returns the primary display label for a workout day.
 * If `effective_session_type` exists and differs from the base type,
 * returns the effective variant's title (e.g. "Upper Body").
 * Otherwise returns the existing `day.type` field as-is.
 *
 * @param {Object} day - Workout day object from the API
 * @returns {string} Primary display label
 */
export function formatSessionLabel(day) {
  if (!day) return '';
  const eff = day.effective_session_type;
  if (eff && EFFECTIVE_TYPE_LABELS[eff]) {
    return EFFECTIVE_TYPE_LABELS[eff].title;
  }
  return day.type || '';
}

/**
 * Returns the subtitle for a reclassified session, or null if standard.
 * The subtitle is shown alongside the main title to communicate
 * the constraint-driven adaptation (e.g. "Pull Focus").
 *
 * @param {Object} day - Workout day object from the API
 * @returns {string|null} Subtitle text, or null if no reclassification
 */
export function getSessionSubtitle(day) {
  if (!day) return null;
  const eff = day.effective_session_type;
  if (eff && EFFECTIVE_TYPE_LABELS[eff]) {
    return EFFECTIVE_TYPE_LABELS[eff].subtitle;
  }
  return null;
}

/**
 * Returns a single combined label: "Upper Body — Pull Focus" or just "Upper Body".
 * Useful for compact display contexts (badges, headers).
 *
 * @param {Object} day - Workout day object from the API
 * @returns {string} Combined label
 */
export function getSessionDisplayName(day) {
  const label = formatSessionLabel(day);
  const subtitle = getSessionSubtitle(day);
  if (subtitle) {
    return `${label} — ${subtitle}`;
  }
  return label;
}
